import React, { useEffect } from 'react';
import { useParkingWizardStore } from 'Stores/ParkingWizardStore';
import { getParkingAvailability, getParkingPriceQuote } from 'Services/Api/_HumanWritten/ParkingService';

export function ParkingReviewStep() {
	const {
		locationId,
		location,
		vehicles,
		quote,
		totalPrice,
		availability,
		setAvailability,
		setQuote,
		setStep,
		setError,
		setLoading,
		isLoading,
	} = useParkingWizardStore();

	useEffect(() => {
		if (!locationId || vehicles.length === 0) return;

		const allStartDates = vehicles.map(v => v.startDate).sort();
		const allEndDates = vehicles.map(v => v.endDate).sort();
		const minStart = allStartDates[0];
		const maxEnd = allEndDates[allEndDates.length - 1];

		setLoading(true);
		Promise.all([
			getParkingAvailability(locationId, minStart, maxEnd),
			getParkingPriceQuote({
				locationId,
				vehicles: vehicles.map(v => ({
					parkingVehicleTypeId: v.parkingVehicleTypeId,
					startDate: v.startDate,
					endDate: v.endDate,
				})),
			}),
		])
			.then(([availabilityRes, quoteRes]) => {
				setAvailability(availabilityRes.days);
				setQuote(quoteRes.vehicles, quoteRes.totalPrice);
				setError(null);
			})
			.catch(() => {
				setError('Failed to load availability or pricing. Please try again.');
			})
			.finally(() => {
				setLoading(false);
			});
	}, [locationId, vehicles, setAvailability, setQuote, setError, setLoading]);

	const handleBack = () => {
		setError(null);
		setStep('vehicles');
	};

	const handleContinue = () => {
		if (!quote) {
			setError('Price quote is not available.');
			return;
		}
		setError(null);
		setStep('payment');
	};

	if (isLoading) {
		return <div className="parking-wizard__loading">Calculating pricing and availability...</div>;
	}

	return (
		<div className="parking-wizard__step parking-review-step">
			<h2>Review Your Parking Reservation</h2>

			<div className="parking-review-step__location">
				<h3>Location</h3>
				<p>{location?.name}</p>
				{location?.address && <p className="text-muted">{location.address}</p>}
			</div>

			<div className="parking-review-step__vehicles">
				<h3>Vehicles</h3>
				<table className="table">
					<thead>
						<tr>
							<th>Vehicle</th>
							<th>Start</th>
							<th>End</th>
							<th>Daily Rate</th>
							<th>Subtotal</th>
						</tr>
					</thead>
					<tbody>
						{quote
							? quote.map(vq => (
									<tr key={vq.parkingVehicleTypeId}>
										<td>
											{vehicles.find(v => v.parkingVehicleTypeId === vq.parkingVehicleTypeId)
												?.parkingVehicleTypeName || vq.parkingVehicleTypeId}
										</td>
										<td>{vq.startDate}</td>
										<td>{vq.endDate}</td>
										<td>${vq.dailyRate.toFixed(2)}</td>
										<td>${vq.subTotal.toFixed(2)}</td>
									</tr>
								))
							: vehicles.map(v => (
									<tr key={v.parkingVehicleTypeId}>
										<td>{v.parkingVehicleTypeName || v.parkingVehicleTypeId}</td>
										<td>{v.startDate}</td>
										<td>{v.endDate}</td>
										<td>-</td>
										<td>-</td>
									</tr>
								))}
					</tbody>
				</table>
			</div>

			{totalPrice !== null && (
				<div className="parking-review-step__total">
					<h3>Total: ${totalPrice.toFixed(2)}</h3>
				</div>
			)}

			<div className="parking-wizard__actions">
				<button type="button" className="btn btn-outline-secondary" onClick={handleBack}>
					Back
				</button>
				<button type="button" className="btn btn-primary" onClick={handleContinue} disabled={!quote}>
					Proceed to Payment
				</button>
			</div>
		</div>
	);
}
